import { LoopOutlined, PlayArrow, Stop, Warning } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, Stack } from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MutableRefObject, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { StyledCircularProgress } from "../../../components/Loading/styles";
import useMicrophone from "../../../hooks/useMicrophone";
import useEnhancedCamera from "../../../hooks/useEnhancedCamera";
import { useThemeWhiteLabel } from "../../../hooks/useThemeWhiteLabel";
import useWebcam from "../../../hooks/useWebcam";
import useWhitelabel from "../../../hooks/useWhitelabel";
import useConnectivity from "../../../hooks/useConnectivity";
import VideoUploadProgress, {
  UploadStatus,
} from "../../../components/VideoUploadProgress";
import { toast } from "react-toastify";

import {
  MailingData,
  MailingResponseData,
} from "../../../shared/models/backend/mailing/mailing";
import { FileTypeEnum } from "../../../shared/models/enums/files.enum";
import useMailingOnBoardingService from "../../../shared/services/useMailingOnBoardingService";
import { Palette } from "../../../shared/theme/palette";
import TelemetryService from "../../../shared/services/TelemetryService";
import ContainerContent from "../components/ContainerContent";
import { EMailingStepsEnum, MailingStepProps } from "../index.types";
import {
  BoldTypography,
  CenterBox,
  CircleBackground,
  MailingTextTypography,
  OnboardingCardLowRadius,
  RecordingLabel,
  SubTitle500Typography,
  SubTitleInterTypography,
  TitleExtraBoldTypography,
  VideoWebcam,
  VideoWebcamContainer,
} from "../styles";
import { formatCPF } from "../utils";
import useFFmpeg from "../../../hooks/useFFmpeg";

type CheckVideoRecordProps = {
  videoRef: MutableRefObject<HTMLVideoElement>;
  videoUrl: string;
  resetVideo: () => void;
  handleUploadVideo: () => void;
  isLoading: boolean;
  mailingData: MailingData;
  keyword: string;
  formattedDate: string;
  whitelabelName: string;
  posterUrl: string;
  showVideo: boolean;
  setShowVideo: (show: boolean) => void;
  uploadStatus: UploadStatus;
  uploadProgress: number;
  isOnline: boolean;
  uploadError?: string;
  onRetryUpload?: () => void;
  isVideoDurationValid: boolean;
};

const CheckVideoRecord = ({
  videoUrl,
  resetVideo,
  videoRef,
  isLoading,
  mailingData,
  keyword,
  posterUrl,
  handleUploadVideo,
  showVideo,
  setShowVideo,
  uploadStatus,
  uploadProgress,
  isOnline,
  uploadError,
  isVideoDurationValid,
  onRetryUpload,
}: CheckVideoRecordProps) => {
  const theme = useThemeWhiteLabel();
  const { loadFFmpeg } = useFFmpeg("");

  useEffect(() => {
    if (showVideo && videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play();
    }
  }, [showVideo, videoRef]);

  useEffect(() => {
    loadFFmpeg();
  }, [loadFFmpeg]);

  return (
    <OnboardingCardLowRadius sx={{ borderRadius: "10px !important" }}>
      <Grid container>
        <Grid item xs={12}>
          <TitleExtraBoldTypography color={theme.primaryColor}>
            Conferir gravação
          </TitleExtraBoldTypography>

          <MailingTextTypography marginTop="-24px">
            Por favor, verifique se o vídeo gravado contém todas as palavras do
            roteiro abaixo:
          </MailingTextTypography>

          <SubTitleInterTypography marginTop="40px" marginBottom="24px">
            Eu <BoldTypography>{mailingData?.nome}</BoldTypography>, portador do
            CPF <BoldTypography>{formatCPF(mailingData?.cpf)}</BoldTypography>,
            autorizo o processo de pré-ativação do benefício do INSS no{" "}
            <BoldTypography>Banco Crefisa</BoldTypography>. A Palavra-chave é{" "}
            <BoldTypography>{keyword}</BoldTypography>.
          </SubTitleInterTypography>
        </Grid>
        <Grid item xs={12} md={8} order={0}>
          {videoUrl && !showVideo && (
            <Box sx={{ position: "relative" }}>
              <img src={posterUrl} style={{ width: "100%", borderRadius: 8 }} />

              <IconButton
                onClick={() => setShowVideo(true)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: "50%",
                }}
              >
                <PlayArrow style={{ fontSize: 48 }} />
              </IconButton>
            </Box>
          )}

          {videoUrl && showVideo && (
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              poster={posterUrl}
              muted
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                borderRadius: 8,
              }}
            />
          )}

          <VideoUploadProgress
            status={uploadStatus}
            progress={uploadProgress}
            errorMessage={uploadError}
            onRetry={onRetryUpload}
            isOnline={isOnline}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          pl={{ xs: "0px", md: "48px" }}
          order={{ xs: 2, md: 1 }}
        >
          <Stack gap="16px" height="100%" justifyContent="center">
            <Button
              color="primary"
              variant="outlined"
              size="large"
              disabled={
                isLoading ||
                uploadStatus === "uploading" ||
                uploadStatus === "processing"
              }
              onClick={resetVideo}
              startIcon={
                <LoopOutlined
                  sx={{
                    color: theme.primaryColor,
                  }}
                />
              }
            >
              Trocar vídeo
            </Button>

            <Button
              color="primary"
              variant="contained"
              size="large"
              disabled={
                isLoading ||
                uploadStatus === "uploading" ||
                uploadStatus === "processing" ||
                !isVideoDurationValid
              }
              startIcon={
                isLoading ? <StyledCircularProgress size={16} /> : undefined
              }
              sx={{ paddingInline: "24px" }}
              onClick={handleUploadVideo}
            >
              {uploadStatus === "success" ? "Continuar" : "Enviar vídeo"}
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} order={{ xs: 1, md: 2 }}>
          <SubTitleInterTypography
            mt={{ xs: "24px", md: "40px" }}
            mb={{ xs: "24px", md: "0px" }}
          >
            Caso queira substituir o vídeo, clique no botão{" "}
            <BoldTypography>Trocar vídeo</BoldTypography>. Para seguir, clique
            no botão <BoldTypography>Enviar vídeo</BoldTypography>.
          </SubTitleInterTypography>
        </Grid>
      </Grid>
    </OnboardingCardLowRadius>
  );
};

const VideoRecordStep = ({ setActiveStep }: MailingStepProps) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [posterUrl, setPosterUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [isFinalizingRecording, setIsFinalizingRecording] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [isVideoDurationValid, setIsVideoDurationValid] = useState(false);
  const [isRecordingNewVideo, setIsRecordingNewVideo] = useState(false);

  // Usar o hook aprimorado em vez do hook original
  const {
    videoRef,
    videoUrl,
    videoStatus,
    recording,
    videoSize,
    error,
    isOnline,
    connectionQuality,
    recordingDuration,
    handleStart,
    handleStop,
    setVideoUrl,
    createVideoBase64,
    resetCamera,
    correlationId,
  } = useEnhancedCamera();

  const { isConnectionSuitable } = useConnectivity();

  const {
    permissionGranted: cameraPermissionGranted,
    hasCamera,
    errorMessage: cameraErrorMessage,
  } = useWebcam();
  const {
    permissionGranted: micPermissionGranted,
    hasMic,
    errorMessage: micErrorMessage,
  } = useMicrophone();
  const { whitelabelName } = useWhitelabel();
  const [mailingData, setMailingData] = useState<MailingResponseData>(null);
  const { sendMailingFile, getMailing } = useMailingOnBoardingService();
  const [activeVideoUrl, setActiveVideoUrl] = useState(videoUrl);

  const permissionsGranted = cameraPermissionGranted && micPermissionGranted;
  const errorMessage = error?.message || cameraErrorMessage || micErrorMessage;
  const hasCameraAndMic = hasCamera && hasMic;

  // Flag para controlar se devemos evitar restaurar o vídeo anterior
  const shouldRestoreVideo = useRef(true);
  const isInitialLoad = useRef(true);

  // Gerenciar o activeVideoUrl de forma mais controlada
  useEffect(() => {
    // Se temos um videoUrl novo e diferente do atual, atualize
    if (videoUrl && videoUrl !== activeVideoUrl) {
      setActiveVideoUrl(videoUrl);
      
      // Reset the flag when a new video is successfully recorded
      if (isRecordingNewVideo) {
        setIsRecordingNewVideo(false);
        shouldRestoreVideo.current = false;
      }
      return;
    }

    // Restaurar vídeo salvo apenas na primeira carga e se não há vídeo ativo
    if (isInitialLoad.current && 
        mailingData?.additionalInfo?.videoUrl && 
        !activeVideoUrl && 
        !isRecordingNewVideo && 
        shouldRestoreVideo.current) {
      
      setVideoUrl(mailingData.additionalInfo.videoUrl);
      setActiveVideoUrl(mailingData.additionalInfo.videoUrl);
      shouldRestoreVideo.current = false;
      isInitialLoad.current = false;
    }
  }, [videoUrl, activeVideoUrl, mailingData?.additionalInfo?.videoUrl, isRecordingNewVideo, setVideoUrl]);

  // Gerar poster apenas quando activeVideoUrl muda e é válido
  useEffect(() => {
    if (activeVideoUrl && activeVideoUrl !== posterUrl) {
      generatePosterUrl(activeVideoUrl);
    }
  }, [activeVideoUrl]);

  // useEffect para recuperar os dados do mailing
  useEffect(() => {
    if (!id) return;

    setIsLoading(true);

    TelemetryService.info(
      "VideoRecordStep",
      "video_record_start",
      { id, correlationId },
      correlationId
    );

    getMailing(id)
      .then((response) => {
        setMailingData(response);

        TelemetryService.info(
          "VideoRecordStep",
          "video_record_start",
          {
            mailingId: id,
            clientName: response?.MailingData?.nome,
            correlationId,
          },
          correlationId
        );
      })
      .catch((error) => {
        TelemetryService.error(
          "VideoRecordStep",
          "video_upload_error",
          { error: error.message, id },
          correlationId
        );

        toast.error("Erro ao carregar os dados. Por favor, tente novamente.");
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      });
  }, [id, correlationId]);

  // Removed useEffect for isCompressing since compression is disabled

  // Gerar uma miniatura do vídeo para pré-visualização
  const generatePosterUrl = async (videoUrl: string) => {
    // Evitar gerar poster para URLs vazias ou inválidas
    if (!videoUrl) {
      return;
    }
    
    setPosterUrl(null);

    try {
      const video = document.createElement("video");

      video.removeAttribute("src");
      video.load();

      video.src = videoUrl;
      video.crossOrigin = "anonymous";
      video.preload = "metadata";
      video.playsInline = true;
      video.muted = true;

      await new Promise<void>((resolve, reject) => {
        video.addEventListener("loadedmetadata", () => resolve(), {
          once: true,
        });
        video.addEventListener(
          "error",
          (e) => reject(new Error(`Video load error: ${e}`)),
          { once: true }
        );
        setTimeout(() => reject(new Error("Video load timeout")), 5000);
      });

      video.load();
      video.currentTime = 2;

      await new Promise<void>((resolve) => {
        video.addEventListener("seeked", () => resolve(), { once: true });
      });

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        throw new Error("Dimensões erradas");
      }

      const canvas = document.createElement("canvas");

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const aspectRatio = video.videoWidth / video.videoHeight;

      const width = isIOS ? 320 : 480;
      canvas.width = width;
      canvas.height = width / aspectRatio;

      const context = canvas.getContext("2d");
      if (!context) throw new Error("Erro ao acessar contexto do canvas");

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const base64 = canvas.toDataURL("image/png");
      setPosterUrl(base64);
    } catch (error) {
      console.error("Falha ao gerar miniatura:", error);
      setPosterUrl(
        "https://knetic.org.uk/wp-content/uploads/2020/07/Video-Placeholder.png"
      );
    }
  };

  // Reiniciar o vídeo
  const resetVideo = () => {
    setIsRecordingNewVideo(true); // Set flag to prevent restoring previous video
    setVideoUrl(null);
    setActiveVideoUrl(null);
    setPosterUrl(null);
    setShowVideo(false);
    setUploadStatus("idle");
    setUploadProgress(0);
    setUploadError(undefined);
    resetCamera();
    setIsFinalizingRecording(false);
    shouldRestoreVideo.current = true; // Permitir restaurar vídeo novamente após reset
    isInitialLoad.current = true; // Permitir nova inicialização após reset
  };

  // Implementação de upload com retry e feedback visual
  const handleUploadVideo = async () => {
    if (!videoUrl) {
      toast.error(
        "Nenhum vídeo para enviar. Por favor, grave um vídeo primeiro."
      );
      return;
    }

    // Verificar se a conexão é adequada
    if (!isConnectionSuitable()) {
      toast.warn(
        "Sua conexão parece estar lenta ou instável. O envio pode levar mais tempo."
      );
    }

    try {
      setUploadStatus("preparing");
      setUploadError(undefined);
      setIsLoading(true);

      // Log de telemetria do início do upload
      TelemetryService.info(
        "VideoRecordStep",
        "video_upload_start",
        {
          id,
          size: videoSize,
          connectionQuality,
          correlationId,
        },
        correlationId
      );

      // Adicionar um pequeno delay simulado para ver o estado 'preparing'
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Converter o vídeo para base64
      const response = await fetch(videoUrl);
      const videoBlob = await response.blob();
      const { base64Content, fileExtension, originalSize } =
        await createVideoBase64(videoBlob);

      // Atualizar estado para 'uploading'
      setUploadStatus("uploading");

      // Simulação de progresso incremental
      const progressUpdater = setInterval(() => {
        setUploadProgress((prev) => {
          const increment = Math.random() * 15;
          const newValue = prev + increment;
          return newValue >= 90 ? 90 : newValue;
        });
      }, 300);

      // Enviar o arquivo com adaptação para conexão lenta
      await sendMailingFile({
        fileContent: base64Content,
        fileName: `${id}-video.${fileExtension}`,
        fileType: FileTypeEnum.PersonalDataVideo,
        publicId: id,
      });

      // Limpar o atualizador de progresso e finalizar
      clearInterval(progressUpdater);
      setUploadProgress(100);
      setUploadStatus("success");

      // Log de telemetria do sucesso
      TelemetryService.info(
        "VideoRecordStep",
        "video_upload_success",
        {
          id,
          size: videoSize,
          originalSize,
          correlationId,
        },
        correlationId
      );

      // Feedback visual de sucesso
      toast.success("Vídeo enviado com sucesso!");

      // Aguardar um momento para o usuário ver o feedback de sucesso
      setTimeout(() => {
        setActiveStep(EMailingStepsEnum.PersonalDataForm);
      }, 1500);
    } catch (error) {
      // Tratar erro de upload
      setUploadStatus("error");
      setUploadError(
        "Não foi possível enviar o vídeo. " +
          (isOnline
            ? "Verifique sua conexão e tente novamente."
            : "Você está offline. O vídeo será salvo localmente.")
      );

      TelemetryService.error(
        "VideoRecordStep",
        "video_upload_error",
        {
          error: error.message,
          id,
          isOnline,
          connectionQuality,
          correlationId,
        },
        correlationId
      );

      console.error("Erro ao enviar vídeo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para tentar novamente o upload após uma falha
  const handleRetryUpload = () => {
    setUploadStatus("idle");
    setUploadProgress(0);
    setUploadError(undefined);
    handleUploadVideo();
  };

  const formattedDate = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  });

  // Feedback visual específico para problemas de permissão
  if (!permissionsGranted) {
    const isMiuiBrowser = /MiuiBrowser/i.test(navigator.userAgent);

    if (isMiuiBrowser) {
      toast.error(
        "O navegador padrão da Xiaomi (MIUI) pode não ser compatível com a gravação de vídeo. Recomendamos o uso de outro navegador."
      );
    }

    return (
      <ContainerContent centered>
        <OnboardingCardLowRadius>
          <SubTitle500Typography marginBlock="64px">
            Favor habilitar o uso da câmera e microfone para gravação do vídeo.
          </SubTitle500Typography>
        </OnboardingCardLowRadius>
      </ContainerContent>
    );
  }

  return (
    <ContainerContent>
      {activeVideoUrl ? (
        <CheckVideoRecord
          videoRef={videoRef}
          videoUrl={activeVideoUrl}
          posterUrl={posterUrl}
          resetVideo={resetVideo}
          handleUploadVideo={handleUploadVideo}
          isLoading={isLoading}
          formattedDate={formattedDate}
          whitelabelName={whitelabelName}
          mailingData={mailingData?.MailingData}
          keyword={mailingData?.keyword}
          showVideo={showVideo}
          setShowVideo={setShowVideo}
          uploadStatus={uploadStatus}
          uploadProgress={uploadProgress}
          isOnline={isOnline}
          uploadError={uploadError}
          onRetryUpload={handleRetryUpload}
          isVideoDurationValid={isVideoDurationValid}
        />
      ) : (
        <OnboardingCardLowRadius sx={{ position: "relative" }}>
          {videoStatus === "recording" && (
            <RecordingLabel>
              <SubTitle500Typography>
                Gravando {recordingDuration > 0 && `(${recordingDuration}s)`}
              </SubTitle500Typography>
            </RecordingLabel>
          )}
          <CircleBackground />

          <VideoWebcamContainer>
            {errorMessage && !videoUrl && (
              <CenterBox>
                <Warning style={{ color: Palette.red }} fontSize="small" />
                <SubTitle500Typography sx={{ marginLeft: "8px" }}>
                  {errorMessage}
                </SubTitle500Typography>
              </CenterBox>
            )}
            {permissionsGranted && hasCameraAndMic && (
              <VideoWebcam audio={false} mirrored />
            )}
          </VideoWebcamContainer>

          <SubTitleInterTypography textAlign="center" marginBottom="48px">
            Eu <BoldTypography>{mailingData?.MailingData?.nome}</BoldTypography>
            , portador do CPF{" "}
            <BoldTypography>
              {formatCPF(mailingData?.MailingData?.cpf)}
            </BoldTypography>
            , autorizo o processo de pré-ativação do benefício do INSS no{" "}
            <BoldTypography>Banco Crefisa</BoldTypography>. A Palavra-chave é{" "}
            <BoldTypography>{mailingData?.keyword}</BoldTypography>.
          </SubTitleInterTypography>

          <Button
            color="primary"
            variant="contained"
            disableTouchRipple
            disabled={isFinalizingRecording}
            size="large"
            sx={{ paddingInline: "24px" }}
            onClick={
              recording
                ? async () => {
                    setIsFinalizingRecording(true);
                    const isValid = await handleStop();
                    setIsVideoDurationValid(isValid);

                    if (!isValid) {
                      setIsFinalizingRecording(false);
                      return;
                    }
                  }
                : () => {
                    // Limpar vídeo anterior quando iniciar nova gravação
                    if (activeVideoUrl) {
                      setIsRecordingNewVideo(true);
                      setActiveVideoUrl(null);
                      setPosterUrl(null);
                      setShowVideo(false);
                      setVideoUrl(null);
                    }
                    handleStart();
                  }
            }
            startIcon={
              recording ? (
                <Stop sx={{ color: "white" }} />
              ) : (
                <PlayArrow sx={{ color: "white" }} />
              )
            }
          >
            {isFinalizingRecording
              ? "Processando vídeo..."
              : recording
              ? "Finalizar gravação"
              : "Iniciar gravação"}
          </Button>
        </OnboardingCardLowRadius>
      )}
    </ContainerContent>
  );
};

export default VideoRecordStep;
